// src/components/admin/UsuarioTable.jsx
import React from 'react';
import { toast } from 'react-toastify';
import ConfirmModal from '../shared/ConfirmModal';

export default function UsuarioTable({
  usuarios = [],
  onDarBaja,
  onReactivar,
  onEditar,
}) {
  const [editando, setEditando] = React.useState(null);
  const [nuevoValor, setNuevoValor] = React.useState({});
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [accionPendiente, setAccionPendiente] = React.useState(null); // { tipo: 'baja'|'reactivar', usuario }

  const lista = Array.isArray(usuarios) ? usuarios : [];
  const isActivo = (u) => u?.activo !== false;

  const fmtFechaBaja = (v) => {
    if (!v) return 'Sin fecha';
    try {
      return new Date(v).toLocaleDateString('es-ES');
    } catch {
      return String(v);
    }
  };

  const iniciarEdicion = (u) => {
    setEditando(u.id);
    setNuevoValor({
      email: u.email,
      role: u.role,
    });
  };

  const guardar = async () => {
    try {
      const payload = {
        email: nuevoValor?.email,
        role: nuevoValor?.role,
      };
      await onEditar?.(editando, payload);
      toast.success('Usuario actualizado');
    } catch (err) {
      toast.error(err?.message || 'Error al actualizar');
    }
    setEditando(null);
  };

  const cancelar = () => setEditando(null);

  const abrirConfirmacion = (usuario, tipo) => {
    setAccionPendiente({ usuario, tipo });
    setConfirmOpen(true);
  };

  const ejecutarAccionConfirmada = async () => {
    if (!accionPendiente?.usuario?.id || !accionPendiente?.tipo) return;
    try {
      if (accionPendiente.tipo === 'baja') {
        await onDarBaja?.(accionPendiente.usuario.id);
        toast.success('Usuario dado de baja');
      } else {
        await onReactivar?.(accionPendiente.usuario.id);
        toast.success('Usuario reactivado');
      }
    } catch (err) {
      toast.error(err?.message || 'No se pudo actualizar el estado');
    }
    setConfirmOpen(false);
    setAccionPendiente(null);
  };

  const onInputKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      guardar();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelar();
    }
  };

  const RoleBadge = ({ role }) => (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
        role === 'admin'
          ? 'bg-blue-50 text-blue-700 border border-blue-200'
          : 'bg-green-50 text-green-700 border border-green-200'
      }`}
    >
      {role === 'admin' ? 'Admin' : 'Empleado'}
    </span>
  );

  const EstadoBadge = ({ activo, fechaBaja }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        activo
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-rose-50 text-rose-700 border border-rose-200'
      }`}
      title={activo ? 'Usuario activo' : `Baja desde ${fmtFechaBaja(fechaBaja)}`}
    >
      <span className={`h-2 w-2 rounded-full ${activo ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      {activo ? 'Activo' : 'Baja'}
    </span>
  );

  return (
    <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200">
      {/* Movil */}
      <ul className="sm:hidden space-y-3">
        {lista.map((u) => {
          const isEditing = editando === u.id;
          const activo = isActivo(u);
          return (
            <li key={u.id} className="rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Email</label>
                  {isEditing ? (
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={nuevoValor.email}
                      onChange={(e) => setNuevoValor((v) => ({ ...v, email: e.target.value }))}
                      onKeyDown={onInputKey}
                      aria-label="Email de usuario"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-800 break-words">{u.email}</p>
                  )}

                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Rol</label>
                    {isEditing ? (
                      <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={nuevoValor.role}
                        onChange={(e) => setNuevoValor((v) => ({ ...v, role: e.target.value }))}
                        onKeyDown={onInputKey}
                        aria-label="Rol de usuario"
                      >
                        <option value="employee">Empleado</option>
                        <option value="admin">Administrador</option>
                      </select>
                    ) : (
                      <RoleBadge role={u.role} />
                    )}
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Estado</label>
                    <EstadoBadge activo={activo} fechaBaja={u?.fecha_baja} />
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        onClick={guardar}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold shadow hover:bg-blue-700"
                        title="Guardar"
                        aria-label="Guardar cambios"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={cancelar}
                        className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-800 text-xs font-semibold shadow hover:bg-gray-300"
                        title="Cancelar"
                        aria-label="Cancelar edicion"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => iniciarEdicion(u)}
                        className="px-3 py-1.5 rounded-lg bg-yellow-100 text-yellow-800 text-xs font-semibold shadow hover:bg-yellow-200"
                        title="Editar"
                        aria-label={`Editar ${u.email}`}
                      >
                        Editar
                      </button>
                      {activo ? (
                        <button
                          onClick={() => abrirConfirmacion(u, 'baja')}
                          className="px-3 py-1.5 rounded-lg bg-rose-100 text-rose-800 text-xs font-semibold shadow hover:bg-rose-200"
                          title="Dar de baja"
                          aria-label={`Dar de baja a ${u.email}`}
                        >
                          Dar de baja
                        </button>
                      ) : (
                        <button
                          onClick={() => abrirConfirmacion(u, 'reactivar')}
                          className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-semibold shadow hover:bg-emerald-200"
                          title="Reactivar"
                          aria-label={`Reactivar ${u.email}`}
                        >
                          Reactivar
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Tabla desktop */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700 text-xs uppercase tracking-wider">
                <th scope="col" className="px-4 py-3 text-left">Email</th>
                <th scope="col" className="px-4 py-3 text-left">Rol</th>
                <th scope="col" className="px-4 py-3 text-left">Estado</th>
                <th scope="col" className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {lista.map((u) => {
                const isEditing = editando === u.id;
                const activo = isActivo(u);
                return (
                  <tr key={u.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2 border-t align-middle">
                      {isEditing ? (
                        <input
                          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={nuevoValor.email}
                          onChange={(e) => setNuevoValor((v) => ({ ...v, email: e.target.value }))}
                          onKeyDown={onInputKey}
                          aria-label="Email de usuario"
                        />
                      ) : (
                        <span className="font-medium text-gray-800">{u.email}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border-t align-middle">
                      {isEditing ? (
                        <select
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={nuevoValor.role}
                          onChange={(e) => setNuevoValor((v) => ({ ...v, role: e.target.value }))}
                          onKeyDown={onInputKey}
                          aria-label="Rol de usuario"
                        >
                          <option value="employee">Empleado</option>
                          <option value="admin">Administrador</option>
                        </select>
                      ) : (
                        <RoleBadge role={u.role} />
                      )}
                    </td>
                    <td className="px-4 py-2 border-t align-middle">
                      <EstadoBadge activo={activo} fechaBaja={u?.fecha_baja} />
                    </td>
                    <td className="px-4 py-2 border-t text-center align-middle">
                      {isEditing ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={guardar}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Guardar"
                            aria-label="Guardar cambios"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={cancelar}
                            className="text-gray-500 hover:text-gray-700 transition"
                            title="Cancelar"
                            aria-label="Cancelar edicion"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => iniciarEdicion(u)}
                            className="text-yellow-600 hover:text-yellow-800 transition"
                            title="Editar"
                            aria-label={`Editar ${u.email}`}
                          >
                            Editar
                          </button>
                          {activo ? (
                            <button
                              onClick={() => abrirConfirmacion(u, 'baja')}
                              className="text-rose-600 hover:text-rose-800 transition"
                              title="Dar de baja"
                              aria-label={`Dar de baja a ${u.email}`}
                            >
                              Baja
                            </button>
                          ) : (
                            <button
                              onClick={() => abrirConfirmacion(u, 'reactivar')}
                              className="text-emerald-600 hover:text-emerald-800 transition"
                              title="Reactivar"
                              aria-label={`Reactivar ${u.email}`}
                            >
                              Reactivar
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {lista.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500 italic">
                    No hay usuarios.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        visible={confirmOpen}
        title={accionPendiente?.tipo === 'reactivar' ? 'Confirmar reactivacion' : 'Confirmar baja'}
        mensaje={
          accionPendiente?.tipo === 'reactivar'
            ? `Reactivar usuario ${accionPendiente?.usuario?.email}?`
            : `Dar de baja usuario ${accionPendiente?.usuario?.email}?`
        }
        confirmText={accionPendiente?.tipo === 'reactivar' ? 'Reactivar' : 'Dar de baja'}
        danger={accionPendiente?.tipo !== 'reactivar'}
        onClose={() => {
          setConfirmOpen(false);
          setAccionPendiente(null);
        }}
        onConfirm={ejecutarAccionConfirmada}
      />
    </div>
  );
}
